import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  DollarSign, 
  Calendar, 
  Target, 
  Clock, 
  CheckCircle,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { format, addDays, endOfWeek, endOfMonth, endOfQuarter, endOfYear, differenceInDays, isAfter, isBefore } from 'date-fns';
import { DateRangeFilter } from '../../components/DateRangeFilter';
import { 
  DATE_FILTER_PRESETS, 
  calculateRevenueProjection,
  filterJobsByDateRange,
  formatJobScheduledDate 
} from '../../services/dateRangeFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

// Projection Card Component
const ProjectionCard = ({ title, amount, jobCount, icon: Icon, color, subtitle, trend }) => (
  <Card className="dark:bg-slate-800/60 border-2 border-slate-700 hover:border-blue-400 transition-all duration-300">
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium dark:text-slate-200">{title}</CardTitle>
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="h-4 w-4 text-white" />
      </div>
    </CardHeader>
    <CardContent>
      <div className="text-3xl font-bold dark:text-white">{formatCurrency(amount)}</div>
      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-muted-foreground dark:text-slate-400">
          {jobCount} job{jobCount !== 1 ? 's' : ''} • {subtitle}
        </p>
        {trend !== undefined && (
          <div className={`flex items-center text-xs ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {trend >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

// Job Row Component
const ScheduledJobRow = ({ job, onClick }) => {
  const scheduledDate = job.scheduled_date ? new Date(job.scheduled_date) : null;
  const daysUntil = scheduledDate ? differenceInDays(scheduledDate, new Date()) : null;
  
  return (
    <div 
      onClick={() => onClick?.(job)}
      className="flex items-center justify-between p-3 rounded-lg bg-slate-800/40 hover:bg-slate-700/50 cursor-pointer transition-colors border border-slate-700/50"
    >
      <div className="flex items-center gap-3">
        <div className={`w-2 h-8 rounded-full ${
          daysUntil !== null && daysUntil <= 7 ? 'bg-emerald-500' : 
          daysUntil !== null && daysUntil <= 14 ? 'bg-yellow-500' : 'bg-blue-500'
        }`} />
        <div>
          <p className="font-medium dark:text-white">{job.customer_name || 'Unknown Customer'}</p>
          <p className="text-xs text-slate-400">
            {job.address || 'No address'} • Stage: {job.stage?.replace(/_/g, ' ') || 'N/A'}
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className="font-bold text-emerald-400">{formatCurrency(job.remaining_balance || job.contract_amount || job.estimated_value)}</p>
        <p className="text-xs text-slate-400">
          {scheduledDate ? format(scheduledDate, 'MMM d') : 'Not scheduled'}
          {daysUntil !== null && daysUntil >= 0 && (
            <span className="ml-1">• {daysUntil === 0 ? 'Today' : `${daysUntil}d`}</span>
          )}
        </p>
      </div>
    </div>
  );
};

export const RevenueProjections = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [opsJobs, setOpsJobs] = useState([]);
  // Legacy timeframe selector (for quick cards)
  const [timeframe, setTimeframe] = useState('14_days'); // 7_days, 14_days, eom, eoq, eoy
  // Shared date range filter
  const [dateFilterPreset, setDateFilterPreset] = useState(DATE_FILTER_PRESETS.NEXT_14_DAYS);
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [projections, setProjections] = useState({
    next7Days: { amount: 0, jobs: [] },
    next14Days: { amount: 0, jobs: [] },
    endOfMonth: { amount: 0, jobs: [] },
    endOfQuarter: { amount: 0, jobs: [] },
    endOfYear: { amount: 0, jobs: [] },
  });
  const [selectedTimeframeJobs, setSelectedTimeframeJobs] = useState([]);
  const [customProjection, setCustomProjection] = useState({ amount: 0, jobs: [], jobCount: 0 });

  // Handle date filter change from shared component
  const handleDateFilterChange = (preset, customRange) => {
    setDateFilterPreset(preset);
    setCustomDateRange(customRange || { from: null, to: null });
    
    // Map shared presets to legacy timeframe selector
    const presetToTimeframe = {
      [DATE_FILTER_PRESETS.NEXT_7_DAYS]: '7_days',
      [DATE_FILTER_PRESETS.NEXT_14_DAYS]: '14_days',
      [DATE_FILTER_PRESETS.END_OF_MONTH]: 'eom',
      [DATE_FILTER_PRESETS.END_OF_QUARTER]: 'eoq',
      [DATE_FILTER_PRESETS.END_OF_YEAR]: 'eoy',
    };
    
    if (presetToTimeframe[preset]) {
      setTimeframe(presetToTimeframe[preset]);
    } else if (preset === DATE_FILTER_PRESETS.CUSTOM_RANGE || preset.startsWith('install_week_')) {
      setTimeframe('custom');
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [jobsRes] = await Promise.all([
        axios.get(`${API}/jobs`, { headers }),
      ]);
      
      setJobs(jobsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load revenue projection data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate projections using shared service
  useEffect(() => {
    if (jobs.length === 0) return;

    // Filter jobs that are in operations stages
    const opsStages = ['scheduled', 'prep', 'production', 'installation', 'final_inspection', 'invoice', 'pending_payment'];
    const filteredOpsJobs = jobs.filter(job => 
      opsStages.includes(job.stage) || 
      (job.stage === 'signed_agreement' && (job.scheduled_date || job.scheduled_completion_date))
    );
    setOpsJobs(filteredOpsJobs);

    // Calculate projections for preset timeframes using shared service
    const newProjections = {
      next7Days: calculateRevenueProjection(filteredOpsJobs, DATE_FILTER_PRESETS.NEXT_7_DAYS),
      next14Days: calculateRevenueProjection(filteredOpsJobs, DATE_FILTER_PRESETS.NEXT_14_DAYS),
      endOfMonth: calculateRevenueProjection(filteredOpsJobs, DATE_FILTER_PRESETS.END_OF_MONTH),
      endOfQuarter: calculateRevenueProjection(filteredOpsJobs, DATE_FILTER_PRESETS.END_OF_QUARTER),
      endOfYear: calculateRevenueProjection(filteredOpsJobs, DATE_FILTER_PRESETS.END_OF_YEAR),
    };

    setProjections(newProjections);
  }, [jobs]);

  // Update selected jobs when timeframe changes
  // Update selected jobs based on filter and custom projection for custom ranges
  useEffect(() => {
    if (timeframe === 'custom') {
      // Use custom projection from shared filter
      const projection = calculateRevenueProjection(opsJobs, dateFilterPreset, customDateRange);
      setCustomProjection(projection);
      setSelectedTimeframeJobs(projection.jobs);
    } else {
      const timeframeMap = {
        '7_days': 'next7Days',
        '14_days': 'next14Days',
        'eom': 'endOfMonth',
        'eoq': 'endOfQuarter',
        'eoy': 'endOfYear',
      };
      
      const key = timeframeMap[timeframe];
      setSelectedTimeframeJobs(projections[key]?.jobs || []);
    }
  }, [timeframe, projections, opsJobs, dateFilterPreset, customDateRange]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleJobClick = (job) => {
    // Navigate to job detail
    window.location.href = `/workspace/jobs/${job.id}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-muted-foreground dark:text-slate-400">Loading revenue projections...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const timeframeLabels = {
    '7_days': 'Next 7 Days',
    '14_days': 'Next 14 Days',
    'eom': `End of ${format(new Date(), 'MMMM')}`,
    'eoq': `End of Q${Math.ceil((new Date().getMonth() + 1) / 3)}`,
    'eoy': `End of ${format(new Date(), 'yyyy')}`,
    'custom': customDateRange.from && customDateRange.to 
      ? `${format(customDateRange.from, 'MMM d')} - ${format(customDateRange.to, 'MMM d')}`
      : 'Custom Range',
  };

  // Get current projection amount based on filter
  const getCurrentProjectionAmount = () => {
    if (timeframe === 'custom') {
      return customProjection.amount;
    }
    const timeframeMap = {
      '7_days': 'next7Days',
      '14_days': 'next14Days',
      'eom': 'endOfMonth',
      'eoq': 'endOfQuarter',
      'eoy': 'endOfYear',
    };
    const key = timeframeMap[timeframe];
    return projections[key]?.amount || 0;
  };

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground dark:text-white">Revenue Projections</h1>
            <p className="text-muted-foreground dark:text-slate-400">
              Forecast revenue based on scheduled job completions
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={refreshing}
              className="dark:bg-slate-700 dark:border-slate-600"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Shared Date Range Filter */}
        <DateRangeFilter
          value={dateFilterPreset}
          customRange={customDateRange}
          onChange={handleDateFilterChange}
          showInstallWeeks={true}
          showCustomRange={true}
          showSaveView={true}
          pageName="revenue_projections"
        />

        {/* Projection Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <ProjectionCard
            title="Next 7 Days"
            amount={projections.next7Days.amount}
            jobCount={projections.next7Days.jobCount}
            icon={Clock}
            color="bg-emerald-600"
            subtitle="EOW"
          />
          <ProjectionCard
            title="Next 14 Days"
            amount={projections.next14Days.amount}
            jobCount={projections.next14Days.jobCount}
            icon={Calendar}
            color="bg-blue-600"
            subtitle="2 weeks"
          />
          <ProjectionCard
            title="End of Month"
            amount={projections.endOfMonth.amount}
            jobCount={projections.endOfMonth.jobCount}
            icon={Target}
            color="bg-purple-600"
            subtitle={format(endOfMonth(new Date()), 'MMM d')}
          />
          <ProjectionCard
            title="End of Quarter"
            amount={projections.endOfQuarter.amount}
            jobCount={projections.endOfQuarter.jobCount}
            icon={TrendingUp}
            color="bg-orange-600"
            subtitle={`Q${Math.ceil((new Date().getMonth() + 1) / 3)}`}
          />
          <ProjectionCard
            title="End of Year"
            amount={projections.endOfYear.amount}
            jobCount={projections.endOfYear.jobCount}
            icon={DollarSign}
            color="bg-yellow-600"
            subtitle={format(new Date(), 'yyyy')}
          />
        </div>

        {/* Detailed View */}
        <Card className="dark:bg-slate-800/60 dark:border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="dark:text-white">Scheduled Revenue</CardTitle>
                <CardDescription className="dark:text-slate-400">
                  Jobs contributing to revenue projection ({timeframeLabels[timeframe]})
                </CardDescription>
              </div>
              <Select value={timeframe} onValueChange={(val) => {
                setTimeframe(val);
                // Sync with shared filter
                const timeframeToPreset = {
                  '7_days': DATE_FILTER_PRESETS.NEXT_7_DAYS,
                  '14_days': DATE_FILTER_PRESETS.NEXT_14_DAYS,
                  'eom': DATE_FILTER_PRESETS.END_OF_MONTH,
                  'eoq': DATE_FILTER_PRESETS.END_OF_QUARTER,
                  'eoy': DATE_FILTER_PRESETS.END_OF_YEAR,
                };
                if (timeframeToPreset[val]) {
                  setDateFilterPreset(timeframeToPreset[val]);
                }
              }}>
                <SelectTrigger className="w-48 dark:bg-slate-700 dark:border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                  <SelectItem value="7_days">Next 7 Days</SelectItem>
                  <SelectItem value="14_days">Next 14 Days</SelectItem>
                  <SelectItem value="eom">End of Month</SelectItem>
                  <SelectItem value="eoq">End of Quarter</SelectItem>
                  <SelectItem value="eoy">End of Year</SelectItem>
                  {timeframe === 'custom' && (
                    <SelectItem value="custom">Custom: {timeframeLabels['custom']}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {/* Summary for selected timeframe */}
            <div className="flex items-center justify-between p-4 mb-4 rounded-lg bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30">
              <div>
                <p className="text-sm text-slate-400">{timeframeLabels[timeframe]} Projection</p>
                <p className="text-2xl font-bold text-white">
                  {formatCurrency(getCurrentProjectionAmount())}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                  {selectedTimeframeJobs.length} Jobs
                </Badge>
              </div>
            </div>

            {/* Jobs List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {selectedTimeframeJobs.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-slate-500 mx-auto mb-3" />
                  <p className="text-slate-400">No scheduled jobs in this timeframe</p>
                  <p className="text-sm text-slate-500">Jobs will appear here when scheduled for completion</p>
                </div>
              ) : (
                selectedTimeframeJobs.map((job) => (
                  <ScheduledJobRow key={job.id} job={job} onClick={handleJobClick} />
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="dark:bg-slate-800/60 dark:border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-emerald-600/20">
                  <CheckCircle className="h-6 w-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Jobs with Dates</p>
                  <p className="text-2xl font-bold text-white">
                    {jobs.filter(j => j.scheduled_date || j.estimated_completion_date).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="dark:bg-slate-800/60 dark:border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-yellow-600/20">
                  <AlertCircle className="h-6 w-6 text-yellow-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Unscheduled (Ops)</p>
                  <p className="text-2xl font-bold text-white">
                    {jobs.filter(j => 
                      ['signed_agreement', 'scheduled', 'production'].includes(j.stage) && 
                      !j.scheduled_date
                    ).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="dark:bg-slate-800/60 dark:border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-blue-600/20">
                  <DollarSign className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Avg Job Value</p>
                  <p className="text-2xl font-bold text-white">
                    {formatCurrency(
                      jobs.length > 0 
                        ? jobs.reduce((sum, j) => sum + (j.contract_amount || j.estimated_value || 0), 0) / jobs.length 
                        : 0
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default RevenueProjections;
